import { useCallback, useEffect, useState } from 'react';
import type { ChangeEventHandler } from 'react';

type ValueElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * A small helper for controlled form inputs.
 *
 * - Mirrors the external value prop (displayValue)
 * - Exposes an onChange handler that updates both local and parent state
 * - Resynchronises displayValue when the parent overwrites value
 */

function useControlledInput<T extends ValueElement>(
  value: string,
  handleValueChange: (newFieldValue: string) => void,
): {
  displayValue: string;
  onChange: ChangeEventHandler<T>;
} {
  const [displayValue, setDisplayValue] = useState<string>(value);

  const onChange: ChangeEventHandler<T> = useCallback(
    (event) => {
      setDisplayValue(event.target.value);
      handleValueChange(event.target.value);
    },
    [
      handleValueChange,
    ],
  );

  // Keep displayValue in sync whenever the parent updates the value prop
  useEffect(() => {
    setDisplayValue(value);
  }, [
    value,
  ]);

  return {
    displayValue,
    onChange,
  };
}

export default useControlledInput;
